import { z } from 'zod';
import { IsoTimestampSchema, UuidSchema } from './common';

export const RAILWAY_COST_RANGES = ['1h', '6h', '24h', '7d', '30d', 'mtd'] as const;
export const RailwayCostRangeSchema = z.enum(RAILWAY_COST_RANGES);
export type RailwayCostRange = z.infer<typeof RailwayCostRangeSchema>;

// --- Project (mapping) ---

export const RailwayProjectSchema = z.object({
  id: UuidSchema,
  railwayProjectId: z.string(),
  name: z.string(),
  applicationId: UuidSchema.nullable(),
  applicationName: z.string().nullable(),
  firstSeenAt: IsoTimestampSchema,
  lastSeenAt: IsoTimestampSchema,
  deletedAtRailway: IsoTimestampSchema.nullable(),
});
export type RailwayProject = z.infer<typeof RailwayProjectSchema>;

export const UpdateRailwayProjectSchema = z.object({
  applicationId: UuidSchema.nullable(),
});
export type UpdateRailwayProject = z.infer<typeof UpdateRailwayProjectSchema>;

// --- Summary query ---

export const RailwayCostsSummaryQuerySchema = z.object({
  range: RailwayCostRangeSchema.default('24h'),
  railwayProjectId: z.string().optional(),
});
export type RailwayCostsSummaryQuery = z.infer<typeof RailwayCostsSummaryQuerySchema>;

// --- Summary response ---

const RailwayCostsTotalsSchema = z.object({
  samples: z.number().int().nonnegative(),
  cpuCostUsd: z.number().nonnegative(),
  memCostUsd: z.number().nonnegative(),
  diskCostUsd: z.number().nonnegative(),
  networkCostUsd: z.number().nonnegative(),
  totalCostUsd: z.number().nonnegative(),
});

const RailwayCostsBucketSchema = z.object({
  bucket: IsoTimestampSchema,
  totalCostUsd: z.number().nonnegative(),
});

const RailwayCostsByProjectSchema = z.object({
  railwayProjectId: z.string(),
  projectName: z.string(),
  applicationId: UuidSchema.nullable(),
  applicationName: z.string().nullable(),
  cpuCostUsd: z.number().nonnegative(),
  memCostUsd: z.number().nonnegative(),
  diskCostUsd: z.number().nonnegative(),
  networkCostUsd: z.number().nonnegative(),
  totalCostUsd: z.number().nonnegative(),
});

export const RailwayCostsSummarySchema = z.object({
  totals: RailwayCostsTotalsSchema,
  timeline: z.array(RailwayCostsBucketSchema),
  byProject: z.array(RailwayCostsByProjectSchema),
});
export type RailwayCostsSummary = z.infer<typeof RailwayCostsSummarySchema>;
export type RailwayCostsByProject = z.infer<typeof RailwayCostsByProjectSchema>;

// --- Sync runs (diagnostics) ---

export const RailwaySyncRunSummarySchema = z.object({
  id: UuidSchema,
  startedAt: IsoTimestampSchema,
  finishedAt: IsoTimestampSchema.nullable(),
  periodStart: IsoTimestampSchema,
  periodEnd: IsoTimestampSchema,
  projectsSeen: z.number().int().nonnegative(),
  samplesInserted: z.number().int().nonnegative(),
  ok: z.boolean(),
  errors: z.array(
    z.object({
      projectId: z.string().optional(),
      message: z.string(),
    }),
  ),
});
export type RailwaySyncRunSummary = z.infer<typeof RailwaySyncRunSummarySchema>;
